import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import {
  Bell,
  Building2,
  Check,
  ChevronDown,
  CircleCheck,
  FileText,
  Gauge,
  Inbox,
  Linkedin,
  Mail,
  MessageCircle,
  MousePointer2,
  Moon,
  MoreHorizontal,
  Paperclip,
  Plus,
  Radar,
  Search,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Sun,
  UserRound,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'

type PortalState = 'replies' | 'market' | 'safeguards'
type PortalTheme = 'dark' | 'light'
type Channel = 'linkedin' | 'email' | 'whatsapp'

type Conversation = {
  initials: string
  name: string
  role: string
  account: string
  preview: string
  time: string
  channel: Channel
  stage: string
  unread?: boolean
}

const THEME_STORAGE_KEY = 'wave99-preview-theme'

export function mapPointerToLocalCoordinate(
  clientCoordinate: number,
  renderedStart: number,
  renderedSize: number,
  localSize: number,
) {
  if (renderedSize <= 0 || localSize <= 0) return 0
  const localCoordinate = ((clientCoordinate - renderedStart) / renderedSize) * localSize
  return Math.min(localSize, Math.max(0, localCoordinate))
}

const conversations: Conversation[] = [
  {
    initials: 'MV',
    name: 'Mara Voss',
    role: 'Quality systems lead',
    account: 'Hawthorn Components',
    preview: 'They: Include our quality lead in the technical note.',
    time: '09:42',
    channel: 'linkedin',
    stage: 'Interested',
    unread: true,
  },
  {
    initials: 'JL',
    name: 'Jonas Linde',
    role: 'Engineering director',
    account: 'Northline Process',
    preview: 'You: Shared the retrofit benchmark and source.',
    time: '08:16',
    channel: 'email',
    stage: 'Contacted',
  },
  {
    initials: 'AN',
    name: 'Ana Novak',
    role: 'Commercial operations',
    account: 'Vantage Motion',
    preview: 'They: We revisit the specification in September.',
    time: 'Tue',
    channel: 'whatsapp',
    stage: 'Replied',
  },
  {
    initials: 'TS',
    name: 'Tomas Saar',
    role: 'Validation manager',
    account: 'Baltic Aseptic',
    preview: 'You: Sent the approved validation note.',
    time: 'Mon',
    channel: 'linkedin',
    stage: 'New',
  },
]

const marketAccounts = [
  {
    name: 'Hawthorn Components',
    category: 'Aseptic systems',
    score: 84,
    signal: 'New line planned for Q4',
  },
  {
    name: 'Northline Process',
    category: 'Retrofit systems',
    score: 76,
    signal: 'Distributor route changed',
  },
  {
    name: 'Vantage Motion',
    category: 'Industrial motion',
    score: 71,
    signal: 'Technical leadership hire',
  },
  {
    name: 'Baltic Aseptic',
    category: 'Validation services',
    score: 69,
    signal: 'Certification window open',
  },
]

function getInitialTheme(): PortalTheme {
  if (typeof window === 'undefined') return 'dark'
  try {
    const saved = window.localStorage.getItem(THEME_STORAGE_KEY)
    if (saved === 'dark' || saved === 'light') return saved
  } catch {
    // The preview safely falls back to the production portal's dark default.
  }
  return 'dark'
}

function ChannelIcon({ channel }: { channel: Channel }) {
  if (channel === 'email') return <Mail aria-hidden="true" />
  if (channel === 'whatsapp') return <MessageCircle aria-hidden="true" />
  return <Linkedin aria-hidden="true" />
}

function ThemeToggle({
  theme,
  onToggle,
}: {
  theme: PortalTheme
  onToggle: () => void
}) {
  return (
    <button
      aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      aria-pressed={theme === 'light'}
      className={`portal-theme-toggle is-${theme}`}
      onClick={onToggle}
      title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      type="button"
    >
      <span aria-hidden="true" className="portal-theme-toggle-track">
        <Sun className="portal-theme-sun" />
        <Moon className="portal-theme-moon" />
        <span className="portal-theme-toggle-thumb" />
      </span>
    </button>
  )
}

function Notifications() {
  return (
    <Popover className="portal-notification-control">
      <PopoverButton aria-label="View notifications" className="portal-icon-button">
        <Bell aria-hidden="true" />
        <i />
      </PopoverButton>
      <PopoverPanel className="portal-notification-popover">
        <header>
          <span><Bell aria-hidden="true" />Activity</span>
          <b>3 new</b>
        </header>
        <div>
          <button type="button">
            <span className="tone-mint"><Radar aria-hidden="true" /></span>
            <span><strong>Plant expansion detected</strong><small>Hawthorn Components · 4m ago</small></span>
          </button>
          <button type="button">
            <span className="tone-violet"><Inbox aria-hidden="true" /></span>
            <span><strong>Qualified reply received</strong><small>Mara Voss · 16m ago</small></span>
          </button>
          <button type="button">
            <span className="tone-amber"><ShieldCheck aria-hidden="true" /></span>
            <span><strong>Sender health reviewed</strong><small>All routes healthy · 1h ago</small></span>
          </button>
        </div>
        <footer>Representative activity · safe sample data</footer>
      </PopoverPanel>
    </Popover>
  )
}

function PortalSidebar({
  activeState,
  onStateChange,
  selectedConversation,
  onConversationChange,
  theme,
  onThemeToggle,
}: {
  activeState: PortalState
  onStateChange: (state: PortalState) => void
  selectedConversation: number
  onConversationChange: (index: number) => void
  theme: PortalTheme
  onThemeToggle: () => void
}) {
  const [query, setQuery] = useState('')
  const [unreadOnly, setUnreadOnly] = useState(false)
  const visibleConversations = useMemo(() => {
    const normalized = query.trim().toLowerCase()
    return conversations
      .map((conversation, index) => ({ conversation, index }))
      .filter(({ conversation }) => {
        if (unreadOnly && !conversation.unread) return false
        if (!normalized) return true
        return [
          conversation.name,
          conversation.account,
          conversation.preview,
          conversation.stage,
        ].some((value) => value.toLowerCase().includes(normalized))
      })
  }, [query, unreadOnly])

  return (
    <aside className="portal-live-sidebar">
      <header className="portal-live-brand-row">
        <button
          aria-label="Open Wave99 reply workspace"
          className="portal-live-brand"
          onClick={() => onStateChange('replies')}
          type="button"
        >
          <span><img alt="" height="32" src="/wave99-mark.png" width="32" /></span>
          <b>Wave99</b>
        </button>
        <button aria-label="Add account" className="portal-quick-add" type="button">
          <Plus aria-hidden="true" />
        </button>
        <Notifications />
        <ThemeToggle onToggle={onThemeToggle} theme={theme} />
      </header>

      <div className="portal-live-controls">
        <button className="portal-live-account" type="button">
          <span><Building2 aria-hidden="true" />All accounts</span>
          <ChevronDown aria-hidden="true" />
        </button>
        <label className="portal-live-search">
          <Search aria-hidden="true" />
          <span className="sr-only">Search conversations</span>
          <input
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search conversations…"
            type="search"
            value={query}
          />
        </label>
        <div className="portal-live-filters">
          <button
            className={!unreadOnly ? 'is-active' : ''}
            onClick={() => setUnreadOnly(false)}
            type="button"
          >
            All
          </button>
          <button
            className={unreadOnly ? 'is-active' : ''}
            onClick={() => setUnreadOnly(true)}
            type="button"
          >
            Unread
          </button>
          <button aria-label="Filter LinkedIn replies" type="button"><Linkedin /></button>
          <button aria-label="Filter email replies" type="button"><Mail /></button>
          <button aria-label="Filter WhatsApp replies" type="button"><MessageCircle /></button>
        </div>
      </div>

      <nav aria-label="Portal preview views" className="portal-live-view-switcher">
        <button
          aria-label="Replies: Triage and handoff"
          aria-current={activeState === 'replies' ? 'page' : undefined}
          className={activeState === 'replies' ? 'is-active' : ''}
          onClick={() => onStateChange('replies')}
          type="button"
        >
          <Inbox aria-hidden="true" /><span><b>Replies</b><small>Triage and handoff</small></span>
        </button>
        <button
          aria-label="Market map: Sources and thesis"
          aria-current={activeState === 'market' ? 'page' : undefined}
          className={activeState === 'market' ? 'is-active' : ''}
          onClick={() => onStateChange('market')}
          type="button"
        >
          <Radar aria-hidden="true" /><span><b>Market map</b><small>Sources and thesis</small></span>
        </button>
        <button
          aria-label="Safeguards: Sending controls"
          aria-current={activeState === 'safeguards' ? 'page' : undefined}
          className={activeState === 'safeguards' ? 'is-active' : ''}
          onClick={() => onStateChange('safeguards')}
          type="button"
        >
          <ShieldCheck aria-hidden="true" /><span><b>Safeguards</b><small>Sending controls</small></span>
        </button>
      </nav>

      <div className="portal-live-list">
        <p><span>Live conversations</span><b>{visibleConversations.length}</b></p>
        {visibleConversations.map(({ conversation, index }) => (
          <button
            className={`${selectedConversation === index && activeState === 'replies' ? 'is-active ' : ''}${conversation.unread ? 'is-unread' : ''}`}
            key={conversation.name}
            onClick={() => {
              onConversationChange(index)
              onStateChange('replies')
            }}
            type="button"
          >
            <span className="portal-live-avatar">{conversation.initials}</span>
            <span className="portal-live-conversation-copy">
              <span>
                <strong>{conversation.name}</strong>
                <em><ChannelIcon channel={conversation.channel} />{conversation.time}</em>
              </span>
              <small>{conversation.preview}</small>
              <span><i>{conversation.account}</i><b>{conversation.stage}</b></span>
            </span>
          </button>
        ))}
      </div>

      <footer className="portal-live-sidebar-footer">
        <span><i />Operated workspace</span>
        <button aria-label="Open workspace settings" type="button"><SlidersHorizontal /></button>
      </footer>
    </aside>
  )
}

function ReplyWorkspace({ conversation }: { conversation: Conversation }) {
  const [channel, setChannel] = useState('All')
  const [draft, setDraft] = useState('Thanks, Mara — I’ll send the approved note and include your quality lead.')
  const [sentMessage, setSentMessage] = useState<string | null>(null)

  useEffect(() => {
    setSentMessage(null)
    setDraft(`Thanks, ${conversation.name.split(' ')[0]} — I’ll send the approved note and include your quality lead.`)
  }, [conversation.name])

  return (
    <section aria-label="Reply triage" className="portal-live-replies">
      <header className="portal-live-chat-header">
        <span className="portal-live-avatar is-large">{conversation.initials}</span>
        <span>
          <strong>{conversation.name}</strong>
          <small>{conversation.role} · {conversation.account}</small>
        </span>
        <b><i />Qualified thread</b>
        <button aria-label="More conversation actions" type="button"><MoreHorizontal /></button>
      </header>

      <div aria-label="Conversation channel" className="portal-live-channel-tabs">
        {['All', 'LinkedIn', 'Email'].map((item) => (
          <button
            className={channel === item ? 'is-active' : ''}
            key={item}
            onClick={() => setChannel(item)}
            type="button"
          >
            {item}
          </button>
        ))}
      </div>

      <div className="portal-live-message-area">
        <div className="portal-live-context-note">
          <Radar aria-hidden="true" />
          <span>
            <b>Reason to talk</b>
            <small>New aseptic line and validation hiring indicate a specification window.</small>
          </span>
          <em>3 sources</em>
        </div>

        <div className="portal-live-date"><span>Today</span></div>

        <article className="portal-live-message is-outbound">
          <p>Noticed the new aseptic line and the quality hiring around it. We have a short validation note for teams setting requirements before the specification is fixed.</p>
          <small>Wave99 · LinkedIn · 09:12</small>
        </article>
        <article className="portal-live-message is-inbound">
          <p>{conversation.preview.replace(/^They:\s*/i, '')}</p>
          <small>{conversation.name.split(' ')[0]} · {conversation.time}</small>
        </article>
        {sentMessage ? (
          <article aria-live="polite" className="portal-live-message is-outbound is-fresh">
            <p>{sentMessage}</p>
            <small>Wave99 · just now</small>
          </article>
        ) : null}

        <div className="portal-live-handoff">
          <CircleCheck aria-hidden="true" />
          <span>
            <strong>Qualified handoff prepared</strong>
            <small>Original evidence, owner, thread, and recommended next step are attached.</small>
          </span>
          <button type="button">Inspect</button>
        </div>
      </div>

      <form
        className="portal-live-composer"
        onSubmit={(event) => {
          event.preventDefault()
          const message = draft.trim()
          if (!message) return
          setSentMessage(message)
          setDraft('')
        }}
      >
        <button aria-label="Attach source" type="button"><Paperclip /></button>
        <input
          aria-label="Draft reply"
          onChange={(event) => setDraft(event.target.value)}
          placeholder="Write a representative reply"
          value={draft}
        />
        <button aria-label="Send draft" disabled={!draft.trim()} type="submit"><Send /></button>
      </form>
    </section>
  )
}

function MarketWorkspace({
  selected,
  onSelect,
}: {
  selected: number
  onSelect: (index: number) => void
}) {
  const account = marketAccounts[selected]
  return (
    <section aria-label="Market reconstruction" className="portal-live-market">
      <header className="portal-live-section-header">
        <span><Radar aria-hidden="true" /><b>Market reconstruction</b></span>
        <div><button className="is-active" type="button">Accounts</button><button type="button">Sources</button></div>
      </header>
      <div className="portal-live-market-body">
        <aside>
          <p><span>Review queue</span><b>38 accounts</b></p>
          {marketAccounts.map((item, index) => (
            <button
              className={selected === index ? 'is-active' : ''}
              key={item.name}
              onClick={() => onSelect(index)}
              type="button"
            >
              <span><Building2 /><b>{item.name}</b><small>{item.category}</small></span>
              <em>{item.score}%</em>
            </button>
          ))}
        </aside>
        <article>
          <header className="portal-live-account-heading">
            <span><small>Account dossier · 014</small><strong>{account.name}</strong><em>{account.category} · Northern Europe</em></span>
            <b>{account.score}% confidence</b>
          </header>
          <div className="portal-live-thesis">
            <header><span>Reason to talk</span><em>3 sources attached</em></header>
            <h4>A specification window may be open before the new line enters validation.</h4>
            <div>
              <span><small>01 · Known</small><b>Quality owns validation risk</b></span>
              <span><small>02 · Found</small><b>{account.signal}</b></span>
              <span><small>03 · Interpreted</small><b>Requirements may still move</b></span>
            </div>
          </div>
          <div className="portal-live-source-list">
            <p><FileText /><span><b>Facilities update</b><small>{account.signal}</small></span><em>Source</em></p>
            <p><UserRound /><span><b>Quality role posted</b><small>Validation ownership added this month</small></span><em>Signal</em></p>
            <p><Sparkles /><span><b>Approved proof matched</b><small>Validation note · revision 3</small></span><em>Ready</em></p>
          </div>
        </article>
      </div>
    </section>
  )
}

function SafeguardsWorkspace() {
  const [capacity, setCapacity] = useState(42)
  const [replyStop, setReplyStop] = useState(true)
  return (
    <section aria-label="Wave99 operating safeguards" className="portal-live-safeguards">
      <header className="portal-live-section-header">
        <span><ShieldCheck aria-hidden="true" /><b>Operating safeguards</b></span>
        <strong><i />Healthy to operate</strong>
      </header>
      <div className="portal-live-safeguard-grid">
        <article className="portal-live-health">
          <div><Gauge /><strong>97</strong><small>health</small></div>
          <span><b>Sender system ready</b><small>4 domains · 8 mailboxes · no active warnings</small></span>
        </article>
        <article className="portal-live-control">
          <header><span><SlidersHorizontal />Daily capacity</span><strong>{capacity}</strong></header>
          <input
            aria-label="Daily capacity"
            max="70"
            min="20"
            onChange={(event) => setCapacity(Number(event.target.value))}
            type="range"
            value={capacity}
          />
          <p><span>Conservative</span><span>Current</span><span>Maximum</span></p>
        </article>
        <article className="portal-live-control portal-live-stop">
          <header><span><Inbox />Reply stop rule</span><strong>{replyStop ? 'Active' : 'Paused'}</strong></header>
          <button
            aria-pressed={replyStop}
            className={replyStop ? 'is-active' : ''}
            onClick={() => setReplyStop((current) => !current)}
            type="button"
          ><i /></button>
          <p>Stop scheduled touches when an inbound reply is detected.</p>
        </article>
        <article className="portal-live-checks">
          <p><Check /><span><b>Claim boundary</b><small>Approved proof only</small></span></p>
          <p><Check /><span><b>Suppression</b><small>Global exclusions synced</small></span></p>
          <p><Check /><span><b>Human escalation</b><small>Ambiguous replies route to operator</small></span></p>
        </article>
      </div>
      <footer className="portal-live-audit">
        <Sparkles /><span><b>Visible to your team</b><small>Last review 12 minutes ago · owner assigned</small></span><button type="button">View audit trail</button>
      </footer>
    </section>
  )
}

function PortalDetail({
  activeState,
  conversation,
  marketIndex,
}: {
  activeState: PortalState
  conversation: Conversation
  marketIndex: number
}) {
  const account = marketAccounts[marketIndex]
  if (activeState === 'market') {
    return (
      <aside aria-label="Selected account details" className="portal-live-detail">
        <header><span><Radar /></span><div><small>Selected account</small><strong>{account.name}</strong></div></header>
        <section>
          <p>Fit and timing</p>
          <div className="portal-live-score"><strong>{account.score}</strong><span><b>High confidence</b><small>4 evidence points</small></span></div>
        </section>
        <section>
          <p>Attached evidence</p>
          <ul>
            <li><Check />Facilities update</li>
            <li><Check />Role ownership</li>
            <li><Check />Approved proof</li>
          </ul>
        </section>
        <footer><button type="button">Open account dossier</button></footer>
      </aside>
    )
  }
  if (activeState === 'safeguards') {
    return (
      <aside aria-label="Safeguard status details" className="portal-live-detail">
        <header><span><ShieldCheck /></span><div><small>Operating status</small><strong>Healthy to operate</strong></div></header>
        <section>
          <p>Active controls</p>
          <ul>
            <li><Check />Reply stop</li>
            <li><Check />Daily capacity</li>
            <li><Check />Global suppression</li>
            <li><Check />Human escalation</li>
          </ul>
        </section>
        <section>
          <p>Next review</p>
          <div className="portal-live-detail-note"><b>Tomorrow · 09:00</b><small>Assigned to Wave99 operations</small></div>
        </section>
      </aside>
    )
  }
  return (
    <aside aria-label="Lead and handoff details" className="portal-live-detail">
      <header><span className="portal-live-avatar is-large">{conversation.initials}</span><div><small>Lead context</small><strong>{conversation.name}</strong></div></header>
      <section>
        <p>Account</p>
        <div className="portal-live-detail-account"><Building2 /><span><b>{conversation.account}</b><small>{conversation.role}</small></span></div>
      </section>
      <section>
        <p>Current stage</p>
        <button className="portal-live-stage" type="button"><i />{conversation.stage}<ChevronDown /></button>
      </section>
      <section>
        <p>Reason to talk</p>
        <div className="portal-live-detail-note"><b>Specification window</b><small>New line and validation hiring indicate requirements may still move.</small></div>
      </section>
      <section>
        <p>Handoff packet</p>
        <ul>
          <li><Check />3 source links</li>
          <li><Check />Original thesis</li>
          <li><Check />Recommended next step</li>
        </ul>
      </section>
      <footer><button type="button">Open qualified handoff</button></footer>
    </aside>
  )
}

export function PortalPreview() {
  const [activeState, setActiveState] = useState<PortalState>('replies')
  const [selectedConversation, setSelectedConversation] = useState(0)
  const [marketIndex, setMarketIndex] = useState(0)
  const [theme, setTheme] = useState<PortalTheme>(getInitialTheme)
  const [engaged, setEngaged] = useState(false)
  const shellRef = useRef<HTMLDivElement>(null)
  const sceneRef = useRef<HTMLDivElement>(null)
  const pointerFrameRef = useRef<number | null>(null)
  const pendingPointerRef = useRef<{ clientX: number; clientY: number } | null>(null)

  useEffect(() => {
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme)
    } catch {
      // Local preference persistence is optional in constrained browser contexts.
    }
  }, [theme])

  useEffect(() => () => {
    if (pointerFrameRef.current !== null) {
      window.cancelAnimationFrame(pointerFrameRef.current)
    }
  }, [])

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const shell = shellRef.current
    if (
      !shell ||
      event.pointerType !== 'mouse' ||
      (typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches)
    ) return

    pendingPointerRef.current = {
      clientX: event.clientX,
      clientY: event.clientY,
    }
    shell.dataset.pointerActive = 'true'
    if (sceneRef.current) sceneRef.current.dataset.pointerActive = 'true'
    if (pointerFrameRef.current !== null) return

    pointerFrameRef.current = window.requestAnimationFrame(() => {
      pointerFrameRef.current = null
      const latestPointer = pendingPointerRef.current
      const currentShell = shellRef.current
      if (!latestPointer || !currentShell) return

      const rect = currentShell.getBoundingClientRect()
      const localWidth = currentShell.clientWidth || rect.width
      const localHeight = currentShell.clientHeight || rect.height
      const localX = mapPointerToLocalCoordinate(
        latestPointer.clientX,
        rect.left,
        rect.width,
        localWidth,
      )
      const localY = mapPointerToLocalCoordinate(
        latestPointer.clientY,
        rect.top,
        rect.height,
        localHeight,
      )
      const x = localX / localWidth - 0.5
      const y = localY / localHeight - 0.5
      for (const target of [currentShell, sceneRef.current]) {
        if (!target) continue
        target.style.setProperty('--portal-rx', `${(-y * 3.7).toFixed(2)}deg`)
        target.style.setProperty('--portal-ry', `${(x * 4.4).toFixed(2)}deg`)
        target.style.setProperty('--portal-mx', `${localX.toFixed(0)}px`)
        target.style.setProperty('--portal-my', `${localY.toFixed(0)}px`)
        target.style.setProperty('--portal-shift-x', `${(x * 10).toFixed(1)}px`)
        target.style.setProperty('--portal-shift-y', `${(y * 8).toFixed(1)}px`)
      }
    })
  }

  const resetPointer = () => {
    const shell = shellRef.current
    if (!shell) return
    if (pointerFrameRef.current !== null) {
      window.cancelAnimationFrame(pointerFrameRef.current)
      pointerFrameRef.current = null
    }
    pendingPointerRef.current = null
    for (const target of [shell, sceneRef.current]) {
      if (!target) continue
      target.style.setProperty('--portal-rx', '0deg')
      target.style.setProperty('--portal-ry', '0deg')
      target.style.setProperty('--portal-shift-x', '0px')
      target.style.setProperty('--portal-shift-y', '0px')
      delete target.dataset.pointerActive
    }
  }

  const selected = conversations[selectedConversation]

  return (
    <div
      className="portal-interaction-shell"
      data-engaged={engaged ? 'true' : 'false'}
      data-portal-theme={theme}
      data-testid="portal-preview"
      onFocusCapture={() => setEngaged(true)}
      onPointerDownCapture={() => setEngaged(true)}
      onPointerEnter={(event) => {
        if (event.pointerType === 'mouse') setEngaged(true)
      }}
      onPointerLeave={resetPointer}
      onPointerMove={handlePointerMove}
      ref={shellRef}
    >
      <div
        className="portal-perspective portal-perspective-4180 portal-perspective-4181 portal-perspective-4182"
        data-engaged={engaged ? 'true' : 'false'}
        data-hit-overscan="40x24"
        data-portal-theme={theme}
        ref={sceneRef}
      >
      <div className="portal-scene-frame">
      <div aria-hidden="true" className="portal-depth-layer depth-one" />
      <div aria-hidden="true" className="portal-depth-layer depth-two" />
      <div aria-hidden="true" className="portal-depth-layer depth-three" />
      <div className="portal-window portal-app-shell">
        <div className="portal-mobile-bar">
          <div className="portal-mobile-brand">
            <img alt="" src="/wave99-mark.png" />
            <b>Wave99</b>
          </div>
          <div className="portal-mobile-nav" aria-label="Portal preview views">
            <button
              aria-pressed={activeState === 'replies'}
              aria-label="Show reply intelligence"
              onClick={() => setActiveState('replies')}
              type="button"
            >
              <Inbox />
            </button>
            <button
              aria-pressed={activeState === 'market'}
              aria-label="Show market reconstruction"
              onClick={() => setActiveState('market')}
              type="button"
            >
              <Radar />
            </button>
            <button
              aria-pressed={activeState === 'safeguards'}
              aria-label="Show sending safeguards"
              onClick={() => setActiveState('safeguards')}
              type="button"
            >
              <ShieldCheck />
            </button>
          </div>
          <ThemeToggle
            onToggle={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')}
            theme={theme}
          />
        </div>
        <PortalSidebar
          activeState={activeState}
          onConversationChange={setSelectedConversation}
          onStateChange={setActiveState}
          onThemeToggle={() => setTheme((current) => current === 'dark' ? 'light' : 'dark')}
          selectedConversation={selectedConversation}
          theme={theme}
        />
        <section aria-label="Wave99 portal workspace" className="portal-live-main">
          <div className="portal-live-preview-status">
            <span><i />Representative portal · safe sample data</span>
            <strong>{activeState === 'replies' ? 'Reply intelligence' : activeState === 'market' ? 'Market reconstruction' : 'Sending safeguards'}</strong>
          </div>
          {activeState === 'replies' ? <ReplyWorkspace conversation={selected} /> : null}
          {activeState === 'market' ? <MarketWorkspace onSelect={setMarketIndex} selected={marketIndex} /> : null}
          {activeState === 'safeguards' ? <SafeguardsWorkspace /> : null}
        </section>
        <PortalDetail
          activeState={activeState}
          conversation={selected}
          marketIndex={marketIndex}
        />
      </div>
      <div aria-hidden="true" className="portal-float-note portal-note-one">
        <Radar />
        <span><small>Signal attached</small><b>Specification window</b></span>
      </div>
      <div aria-hidden="true" className="portal-float-note portal-note-two">
        <CircleCheck />
        <span><small>Reply triaged</small><b>Handoff ready</b></span>
      </div>
      </div>
      </div>
      <div aria-hidden="true" className="portal-resting-veil" />
      <div aria-hidden="true" className="portal-engagement-cue">
        <MousePointer2 />
        <span>
          <b>Interactive operating system</b>
          <small>Move across the portal to explore each stage</small>
        </span>
      </div>
      <div aria-hidden="true" className="portal-cursor-gloss"><i /></div>
    </div>
  )
}
